<template>
  <div>
    <ul
      class="toggle-switch"
      :class="{'square' : defaultOptions.layout.squareCorners}"
      :style="toggleSwitchStyle"
    >
      <li
        :style="itemStyle"
        v-for="(item, index) in defaultOptions.config.items"
        :key="index"
        :class="{'gutter': defaultOptions.layout.gutter > 0}"
      >
        <input
          :disabled="defaultOptions.config.disabled || disabled"
          :id="item.value + group" :value="item.value"
          :name="name"
          type="radio"
          v-on:click="toggle"
          style="display: none"
        >
        <label
          v-if="item.value === selectedItem"
          :style="labelStyleSelected(item.color, item.backgroundColor)"
          :class="{ active: !defaultOptions.config.disabled || disabled}"
          class="selected"
          :for="item.value + group"
          type="radio"
        >
          <LucideIcon 
            icon="Check" 
            class="checkIcon"
            v-if="defaultOptions.layout.checkIcon && item.value === selectedItem"
          />
          <LucideIcon 
            :icon="item.icon.name" 
            :style="{'display': item.icon.display}"
            v-if="item.icon && item.icon.name"
          />
          <div v-if="item.html" v-html="item.html"></div>
          <span v-else>{{ item.name }}</span>
        </label>
        <label
          v-else
          :style="labelStyle"
          :class="{active: !defaultOptions.config.disabled || disabled}"
          :for="item.value + group"
          type="radio"
        >
          <LucideIcon 
            icon="Check" 
            class="checkIcon"
            v-if="defaultOptions.layout.checkIcon && item.value === selectedItem"
          />
          <LucideIcon 
            :icon="item.icon.name" 
            :style="{'display': item.icon.display}"
            v-if="item.icon && item.icon.name"
          />
          <div v-if="item.html" v-html="item.html"></div>
          <span v-else>{{ item.name }}</span>
        </label>
      </li>
    </ul>
  </div>
</template>

<script>
import { defineComponent } from 'vue';

const s = x => x + 's'
const rem = v => v + 'rem'

export default defineComponent({
  name: 'ToggleElement',
  props: {
    options: {
      type: Object,
      required: false
    },
    value: {
      type: String,
      required: false
    },
    name: {
      type: String,
      required: false
    },
    group: {
      type: String,
      required: false,
      default: ''
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false
    },
    html: {
      type: String,
      required: false
    },
  },
  created () {
    this.defaultOptions = {
      layout: {
        color: '#ddd',
        backgroundColor: '#eee',
        selectedColor: 'green',
        selectedBackgroundColor: 'red',
        borderColor: 'lightgray',
        fontWeight: 'normal',
        fontWeightSelected: 'bold',
        squareCorners: false,
        noBorder: false,
        checkIcon: false,
        gutter: 0,
        padding: 0
      },
      config: {
        delay: 0.4,
        preSelected: 'unknown',
        disabled: false,
        items: []
      }
    }
  },
  mounted () {
    if (this.options !== null && this.options !== undefined) {
      this.mergeDefaultOptionsWithProp(this.options)
    }
    if (this.defaultOptions.config.preSelected !== 'unknown') {
      this.selectedItem = this.defaultOptions.config.preSelected
      this.$emit('input', this.selectedItem)
    } else if (this.value) {
      this.selectedItem = this.value
      this.$emit('input', this.selectedItem)
    }
  },
  data () {
    return {
      selected: false,
      selectedItem: 'unknown',
      defaultOptions: Object
    }
  },
  computed: {
    toggleSwitchStyle () {
      return {
        display: 'flex',
      }
    },
    itemStyle () {
      return {
        flex: 1,
        textAlign: 'left',
        margin: 'auto ' + (this.defaultOptions.layout.gutter > 0 ? this.defaultOptions.layout.gutter + 'px' : '')
      }
    },
    labelStyle () {
      return {
        padding: (this.defaultOptions.layout.padding > 0 ? this.defaultOptions.layout.padding : 5) + 'px',
        borderColor: this.defaultOptions.layout.noBorder ? 'transparent' : this.defaultOptions.layout.borderColor,
        backgroundColor: this.defaultOptions.layout.backgroundColor,
        color: this.defaultOptions.layout.color,
        fontWeight: this.defaultOptions.layout.fontWeight,
        transition: s(this.defaultOptions.config.delay)
      }
    }
  },
  methods: {
    toggle(event) {
      if (!this.defaultOptions.config.disabled) {
        this.selected = true
        this.selectedItem = event.target.id.replace(this.group, '')
        this.$emit('update:value', this.selectedItem);
        this.$emit('change', this.selectedItem);
      }
    },
    labelStyleSelected (color, backgroundColor) {
      return {
        padding: (this.defaultOptions.layout.padding > 0 ? this.defaultOptions.layout.padding : 5) + 'px',
        borderColor: this.defaultOptions.layout.noBorder ? 'transparent' : this.defaultOptions.layout.borderColor,
        fontWeight: this.defaultOptions.layout.fontWeightSelected,
        backgroundColor: backgroundColor !== undefined ? backgroundColor : this.defaultOptions.layout.selectedBackgroundColor,
        color: color !== undefined ? color : this.defaultOptions.layout.selectedColor,
        transition: s(this.defaultOptions.config.delay),
      }
    },
    mergeDefaultOptionsWithProp (options) {
      const result = this.defaultOptions
      for (const option in options) {
        if (options[option] !== null && typeof (options[option]) === 'object') {
          for (const subOption in options[option]) {
            if (options[option][subOption] !== undefined && options[option][subOption] !== null) {
              result[option][subOption] = options[option][subOption]
            }
          }
        } else {
          result[option] = options[option]
        }
      }
    }
  },
  watch: {
    value (val) {
      this.selectedItem = val
    },
    options (val) {
      if (val !== null && val !== undefined) {
        this.mergeDefaultOptionsWithProp(val)
      }
    }
  }
});
</script>

<style lang="scss" scoped>
ul {
  list-style: none;
}

label {
  margin: var(--space-0);
}

.toggle-switch {
  padding: 0;
  margin: var(--space-0);
  display: inline-flex;
}
.toggle-switch li.gutter label {
  border: 1px solid;
  border-radius: $br-sm;
}

.square li:not(.gutter):first-child label {
  border-top-left-radius: 0 !important;
  border-bottom-left-radius: 0 !important;
}
.square li:not(.gutter):last-child label {
  border-top-right-radius: 0 !important;
  border-bottom-right-radius: 0 !important;
}

.toggle-switch li {
  position:relative;
}

.toggle-switch li:first-child label {
  border: 1px solid;
  border-top-left-radius: 5px;
  border-bottom-left-radius: 5px;
}

.toggle-switch li:last-child label {
  border-right: 1px solid;
  border-top: 1px solid;
  border-bottom: 1px solid;
  border-top-right-radius: 5px;
  border-bottom-right-radius: 5px;
}

.toggle-switch label, .toggle-switch input {
  display:block;
  top:0;
  left:0;
  right:0;
  bottom:0;
}

.toggle-switch input[type="radio"] {
  opacity:0.01;
  z-index:100;
}

.toggle-switch li:not(:first-child):not(:last-child) label {
  border-right: 1px solid;
  border-top: 1px solid;
  border-bottom: 1px solid;
}

.toggle-switch .checkIcon {
  margin: auto 10px;
}

.active {
  cursor: pointer;
}

</style>
